import {
  FileEntityType,
  FileExtension,
  FileImageCropper,
  IFileRequest,
} from "@/integration/application/interfaces/file.interface.ts";
import fileService from "@integration/application/service/file.service.ts";
import { useQueryClient } from "@tanstack/react-query";
import { QUERY_KEYS } from "@integration/factory/queryKeys.ts";

const useFile = () => {
  const queryClient = useQueryClient();
  const { postSave, putUpdate } = fileService();

  const uploadFile = async (base64Image: string, file: FileImageCropper) => {
    // Ensure we properly extract the base64 data regardless of prefix format
    let base64Data = base64Image;
    if (base64Image.includes("base64,")) {
      base64Data = base64Image.split("base64,")[1];
    }

    const fileRequest: IFileRequest = {
      base64: base64Data,
      extension: FileExtension.JPEG,
      entityType: file.entity_type,
      entityId: file.entity_id,
      businessType: file.business_type,
      fileType: "IMAGE",
    };

    try {
      if (file.id) {
        await putUpdate(file.id, fileRequest);
      } else {
        await postSave(fileRequest);
      }

      // Invalidate queries based on the entity type and business type
      // This ensures the UI updates immediately with the new image
      switch (file.entity_type) {
        case FileEntityType.TEAM_SPONSOR:
          // Invalidate team sponsors and team queries
          queryClient.invalidateQueries({ queryKey: ['teamSponsors'] });
          queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.TEAM.root] });
          break;
        case FileEntityType.TEAM:
          // Invalidate team queries
          queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.TEAM.root] });
          break;
        case FileEntityType.USER:
          // Invalidate user queries
          queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.USER.root] });
          break;
        case FileEntityType.CHAMPIONSHIP:
          queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.CHAMPIONSHIP.root] });
          break;
        default:
          // For other entity types, invalidate all queries
          queryClient.invalidateQueries();
      }
    } catch (error) {
      throw new Error("Image Upload Error");
    }
  };

  return { uploadFile };
};

export default useFile;
